import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  RUNTIMES,
  RUNTIME_TOKENS,
  RUNTIME_WAREHOUSE_CONNECTIONS,
} from '@elementary/common-ui/constants/queryKeys';
import useSnackbar from 'shared/hooks/useSnackbar';
import * as api from '../api';
import { CreateRuntimeTokenRequest, UpdateRuntimeRequest } from '../types';

const useRuntime = (runtimeId: string) => {
  const queryClient = useQueryClient();
  const { openSnackbar } = useSnackbar();

  const {
    data: runtime,
    isLoading,
    isError,
  } = useQuery({
    queryKey: [RUNTIMES, runtimeId],
    queryFn: () => api.getRuntime(runtimeId),
    enabled: !!runtimeId,
  });

  const {
    data: tokens = [],
    isLoading: isLoadingTokens,
    isError: isTokensError,
    refetch: refetchTokens,
  } = useQuery({
    queryKey: [RUNTIME_TOKENS, runtimeId],
    queryFn: () => api.getRuntimeTokens(runtimeId),
    enabled: !!runtimeId,
  });

  const {
    data: warehouseConnections = [],
    isLoading: isLoadingWarehouseConnections,
    isError: isWarehouseConnectionsError,
    refetch: refetchWarehouseConnections,
  } = useQuery({
    queryKey: [RUNTIME_WAREHOUSE_CONNECTIONS],
    queryFn: api.getWarehouseConnections,
  });

  const handleSuccess = (message: string) => {
    openSnackbar({ message, variant: 'success' });
    queryClient.invalidateQueries({ queryKey: [RUNTIMES] });
    queryClient.invalidateQueries({ queryKey: [RUNTIME_TOKENS] });
    queryClient.invalidateQueries({
      queryKey: [RUNTIME_WAREHOUSE_CONNECTIONS],
    });
  };

  const handleError = (message: string) => {
    openSnackbar({ message, variant: 'error' });
  };

  const updateRuntimeMutation = useMutation({
    mutationFn: (request: UpdateRuntimeRequest) =>
      api.updateRuntime(runtimeId, request),
    onSuccess: (updatedRuntime) => {
      queryClient.setQueryData([RUNTIMES, runtimeId], updatedRuntime);
      handleSuccess('Runtime updated');
    },
    onError: () => handleError('Failed to update runtime'),
  });

  const deleteRuntimeMutation = useMutation({
    mutationFn: () => api.deleteRuntime(runtimeId),
    onSuccess: () => {
      openSnackbar({ message: 'Runtime deleted', variant: 'success' });
      // Refetching this runtime or its tokens after deletion would 404.
      queryClient.invalidateQueries({ queryKey: [RUNTIMES], exact: true });
      queryClient.invalidateQueries({
        queryKey: [RUNTIME_WAREHOUSE_CONNECTIONS],
      });
    },
    onError: () => handleError('Failed to delete runtime'),
  });

  const createTokenMutation = useMutation({
    mutationFn: (request: CreateRuntimeTokenRequest) =>
      api.createRuntimeToken(runtimeId, request),
    onSuccess: () => handleSuccess('Runtime token created'),
    onError: () => handleError('Failed to create runtime token'),
  });

  const revokeTokenMutation = useMutation({
    mutationFn: (tokenId: string) => api.revokeRuntimeToken(runtimeId, tokenId),
    onSuccess: () => handleSuccess('Runtime token revoked'),
    onError: () => handleError('Failed to revoke runtime token'),
  });

  return {
    runtime,
    isLoading,
    isError,
    tokens,
    isLoadingTokens,
    isTokensError,
    refetchTokens,
    warehouseConnections,
    isLoadingWarehouseConnections,
    isWarehouseConnectionsError,
    refetchWarehouseConnections,
    updateRuntime: updateRuntimeMutation.mutateAsync,
    isUpdating: updateRuntimeMutation.isLoading,
    deleteRuntime: deleteRuntimeMutation.mutateAsync,
    isDeleting: deleteRuntimeMutation.isLoading,
    createToken: createTokenMutation.mutateAsync,
    isCreatingToken: createTokenMutation.isLoading,
    revokeToken: revokeTokenMutation.mutateAsync,
    isRevokingToken: revokeTokenMutation.isLoading,
  };
};

export default useRuntime;
